import React from 'react';
import { Settings, Eye, EyeOff, RotateCcw, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { TableHead } from '../components/ui/table';
import { cn } from '../lib/utils';

// Enhanced resizer handle component with touch support
const ColumnResizer = ({ 
  columnId, 
  onResizeStart, 
  onAutoFit, 
  isResizing,
  className 
}) => {
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onResizeStart(columnId, e.clientX);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.touches.length > 0) {
      onResizeStart(columnId, e.touches[0].clientX);
    }
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAutoFit(columnId);
  };

  return (
    <div
      className={cn(
        "absolute right-0 top-0 h-full w-2 cursor-col-resize bg-transparent hover:bg-blue-400/50 transition-colors z-10",
        "touch:w-3 touch:right-[-6px]", // Wider touch target on mobile
        isResizing && "bg-blue-500 shadow-sm",
        className
      )}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onDoubleClick={handleDoubleClick}
      role="separator"
      aria-label={`Resize ${columnId} column`}
    />
  );
};

// Sort Indicator Component for table headers - compact version with white color for blue background
const SortIndicator = ({ direction, isHovered }) => {
  // When actively sorted ascending
  if (direction === 'asc') {
    return (
      <ChevronUp 
        className="w-3.5 h-3.5 text-white" 
        strokeWidth={2.5}
      />
    );
  }
  
  // When actively sorted descending
  if (direction === 'desc') {
    return (
      <ChevronDown 
        className="w-3.5 h-3.5 text-white" 
        strokeWidth={2.5}
      />
    );
  }
  
  // Neutral state - show subtle indicator only on hover
  if (!isHovered) {
    return null; // Hide when not hovering to save space
  }
  
  return (
    <ChevronsUpDown 
      className="w-3.5 h-3.5 opacity-70 text-white"
      strokeWidth={1.5}
    />
  );
};

// Enhanced resizable table header component with improved keyboard support AND sorting
export const ResizableTableHead = ({ 
  column, 
  children,
  onResizeStart,
  onAutoFit,
  onKeyboardResize,
  isResizing,
  // Sorting props
  sortDirection = null,
  onSort,
  className,
  ...props 
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const isSortable = column.sortable !== false;
  const isSorted = sortDirection !== null;
  
  const handleKeyDown = (e) => {
    // Enhanced keyboard navigation: Arrow keys resize by 10px, Shift+Arrow by 30px
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onKeyboardResize(column.id, 'decrease', e.shiftKey);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onKeyboardResize(column.id, 'increase', e.shiftKey);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // If sortable, sort on Enter/Space, otherwise auto-fit
      if (isSortable && onSort) {
        onSort(column.id);
      } else {
        onAutoFit(column.id);
      }
    }
  };
  
  const handleClick = (e) => {
    // Only sort if not clicking on the resize handle
    if (isSortable && onSort && !e.target.closest('.resize-handle')) {
      onSort(column.id);
    }
  };

  // Determine minimum width based on column type
  const getMinWidth = () => {
    // Short column names need appropriate min-width
    const columnMinWidths = {
      'state': '75px',
      'status': '90px', 
      'county': '85px',
      'assignee': '100px'
    };
    return columnMinWidths[column.id?.toLowerCase()] || '90px';
  };

  return (
    <TableHead
      className={cn(
        "relative select-none group transition-colors",
        "focus-within:outline-none focus-within:ring-2 focus-within:ring-white/30 focus-within:ring-opacity-50",
        isSortable && "cursor-pointer",
        // Use subtle brightness change on hover instead of changing color
        isSortable && "hover:brightness-110",
        // Sorted state - slightly lighter to indicate active sort
        isSorted && "brightness-105",
        className
      )}
      style={{ 
        width: column.width,
        minWidth: getMinWidth(),
        maxWidth: '560px',
        paddingRight: '12px' // Space for resize handle
      }}
      data-column-id={column.id}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="columnheader"
      aria-sort={
        sortDirection === 'asc' ? 'ascending' : 
        sortDirection === 'desc' ? 'descending' : 
        undefined
      }
      aria-label={`${column.title} column${isSortable ? ', sortable' : ''}`}
      data-testid={`sortable-header-${column.id}`}
      {...props}
    >
      <div className="flex items-center gap-1 pr-2">
        {/* Label - inherits white color from parent CSS */}
        <span className="font-semibold text-sm whitespace-nowrap">
          {children}
        </span>
        {/* Sort icon - only shows when sorted or hovered */}
        {isSortable && (isSorted || isHovered) && (
          <SortIndicator 
            direction={sortDirection} 
            isHovered={isHovered}
          />
        )}
      </div>
      <ColumnResizer
        columnId={column.id}
        onResizeStart={onResizeStart}
        onAutoFit={onAutoFit}
        isResizing={isResizing}
        className="resize-handle"
      />
    </TableHead>
  );
};

// Column visibility menu
export const ColumnVisibilityMenu = ({ 
  columns, 
  columnVisibility, 
  onToggleVisibility,
  onReset 
}) => {
  const visibleCount = Object.values(columnVisibility).filter(Boolean).length;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="ml-auto">
          <Settings className="h-4 w-4 mr-2" />
          Columns
          <span className="ml-2 text-xs text-muted-foreground">
            ({visibleCount}/{columns.length})
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-semibold">
          Column Visibility
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {columns.map((column) => {
          const isVisible = columnVisibility[column.id];
          const canHide = visibleCount > 1; // Prevent hiding all columns
          
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              className="capitalize"
              checked={isVisible}
              onCheckedChange={() => canHide && onToggleVisibility(column.id)}
              disabled={isVisible && !canHide}
            >
              <div className="flex items-center space-x-2">
                {isVisible ? (
                  <Eye className="h-4 w-4" />
                ) : (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                )}
                <span>{column.title}</span>
              </div>
            </DropdownMenuCheckboxItem>
          );
        })}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onReset}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Columns
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Resize indicator/guide
export const ResizeGuide = ({ isResizing, position }) => {
  if (!isResizing) return null;
  
  return (
    <div
      className="fixed top-0 bottom-0 w-0.5 bg-blue-500 z-50 pointer-events-none"
      style={{ left: position }}
    />
  );
};